import { useState, useEffect, useReducer, useCallback } from 'react';
import { VideoPlayerActionType, videoPlayer, } from './videoPlayer';
export const useVideoPlayer = ({ autoplay = false, playOnHover, disabled, disabledUntilHover, toggleVolumeOnClick, playButtonOnLoad, includeSound, overrideMute, onTogglePlay, onToggleMute, title, inForeground, }) => {
    const [isHoverable, setIsHoverable] = useState(false);
    useEffect(() => {
        setIsHoverable(typeof window !== 'undefined'
            ? window.matchMedia('(hover: hover').matches
            : false);
    }, []);
    const initialState = {
        isPlaying: false,
        isMuted: autoplay || playOnHover || !includeSound || disabled || false,
        loop: false,
        showFullControls: false,
        showPlayButtonOnly: (!autoplay && playButtonOnLoad) || false,
        showDisabledOverlay: disabled || disabledUntilHover || false,
        isMouseOver: false,
        isInFocus: false,
        isInForeground: false,
        wasPlayingInForeground: false,
    };
    const [state, dispatch] = useReducer(videoPlayer, initialState);
    useEffect(() => {
        if (overrideMute !== undefined && overrideMute !== state.isMuted) {
            dispatch({ type: VideoPlayerActionType.TOGGLE_MUTE });
        }
    }, [overrideMute, state.isMuted]);
    useEffect(() => {
        if (disabled || disabledUntilHover) {
            if ((!state.isMouseOver || disabled) &&
                (!state.isInFocus || disabled) &&
                !state.showDisabledOverlay) {
                dispatch({
                    type: VideoPlayerActionType.DISABLE,
                });
            }
            else if (disabled &&
                (state.isMouseOver || state.isInFocus) &&
                state.showFullControls) {
                if (state.isPlaying) {
                    dispatch({ type: VideoPlayerActionType.TOGGLE_PLAY });
                }
                dispatch({
                    type: VideoPlayerActionType.TOGGLE_FULL_CONTROLS,
                });
            }
        }
    }, [
        disabled,
        disabledUntilHover,
        state.isMouseOver,
        state.showDisabledOverlay,
        state.isInFocus,
        state.showFullControls,
        state.isPlaying,
    ]);
    useEffect(() => {
        if (!disabled && !disabledUntilHover && state.showDisabledOverlay) {
            dispatch({
                type: VideoPlayerActionType.ENABLE,
                payload: { autoplay },
            });
        }
    }, [autoplay, disabled, disabledUntilHover, state.showDisabledOverlay]);
    const payload = {
        playOnHover,
        disabled,
        disabledUntilHover,
        autoplay,
        toggleVolumeOnClick,
        isHoverable,
        title,
    };
    const togglePlayButton = () => {
        onTogglePlay === null || onTogglePlay === void 0 ? void 0 : onTogglePlay();
        dispatch({ type: VideoPlayerActionType.TOGGLE_PLAY, payload });
    };
    const toggleVolume = () => {
        onToggleMute === null || onToggleMute === void 0 ? void 0 : onToggleMute();
        dispatch({ type: VideoPlayerActionType.TOGGLE_MUTE, payload });
    };
    const onClick = () => {
        if (!disabled) {
            if (isHoverable) {
                if (toggleVolumeOnClick && includeSound) {
                    toggleVolume();
                }
                else
                    togglePlayButton();
            }
            else {
                if (state.showPlayButtonOnly) {
                    togglePlayButton();
                }
                else {
                    dispatch({
                        type: VideoPlayerActionType.TOGGLE_FULL_CONTROLS,
                        payload,
                    });
                }
            }
        }
    };
    const onMouseEnter = () => {
        if (!state.isMouseOver && isHoverable) {
            dispatch({ type: VideoPlayerActionType.MOUSE_ENTER, payload });
        }
    };
    const onMouseLeave = () => {
        dispatch({ type: VideoPlayerActionType.MOUSE_LEAVE, payload });
    };
    const onFocus = () => {
        dispatch({ type: VideoPlayerActionType.FOCUS, payload });
    };
    const onBlur = () => {
        dispatch({ type: VideoPlayerActionType.BLUR, payload });
    };
    useEffect(() => {
        if (inForeground && !state.isInForeground) {
            dispatch({
                type: VideoPlayerActionType.IN_FOREGROUND,
                payload: { autoplay },
            });
        }
        if (!inForeground && state.isInForeground) {
            dispatch({
                type: VideoPlayerActionType.OUT_FOREGROUND,
            });
        }
    }, [autoplay, inForeground, state.isInForeground]);
    const play = useCallback(() => {
        dispatch({ type: VideoPlayerActionType.PLAY, payload: { autoplay } });
    }, [autoplay]);
    const pause = useCallback(() => {
        dispatch({ type: VideoPlayerActionType.PAUSE });
    }, []);
    return {
        onMouseEnter,
        onMouseLeave,
        onFocus,
        onBlur,
        onClick,
        onKeyboardEnter: onClick,
        togglePlayButton,
        toggleVolume,
        pause,
        play,
        ...state,
    };
};
